/**
 * PageSettingsPanel Component
 *
 * Read-only "Page Drivers" view of the Page side panel; pages are edited in the Page Setup Wizard. A
 * "Page Settings" selector switches between the page's driver set for each configured dataset (Galaxy,
 * HCTR) and, when a denominator label is set, the page's % denominator: its label and its denominator
 * drivers, for the datasets denominators support.
 */

import './PageSettingsPanel.scss';

import { DENOMINATOR_DATASETS, DenominatorLabels, denominatorNotEnabledMessage } from '@constants/page';
import { usePageContext } from '@context/PageContext';
import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { useMemo } from 'react';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { PAGE_SETTINGS_DENOMINATOR_VIEW, PageSettingsPanelProps, PageSettingsView } from 'types/PageSidePanel.types';

/** The dataset whose denominator drivers the page holds today. */
const DENOMINATOR_DATASET = DatasetSource.GALAXY;

const PageSettingsPanel = ({ selectedView, onViewChange }: PageSettingsPanelProps) => {
  const { currentPageDriverOptions, modifiedPageProperties, configuredDatasets, denominatorDriverOptions } =
    usePageContext();
  const { denominator_label: denominatorLabel } = modifiedPageProperties || {};

  /**
   * Selector options: each configured dataset (in DatasetSource order, falling back to Galaxy when the page
   * has no driver sets), followed by the denominator when the page has a denominator label.
   */
  const viewOptions = useMemo(() => {
    const datasets = Object.values(DatasetSource).filter((ds) => configuredDatasets.includes(ds));
    const options: Array<{ value: PageSettingsView; label: string }> = (
      datasets.length ? datasets : [DatasetSource.GALAXY]
    ).map((ds) => ({ value: ds, label: resolveDatasetSourceLabel(ds) }));
    if (denominatorLabel) {
      options.push({ value: PAGE_SETTINGS_DENOMINATOR_VIEW, label: DenominatorLabels[denominatorLabel] });
    }
    return options;
  }, [configuredDatasets, denominatorLabel]);

  // Fall back to the first option when the selection is unset or no longer offered (e.g. the denominator
  // label was removed, or a different page loaded).
  const activeView = viewOptions.some((option) => option.value === selectedView) ? selectedView! : viewOptions[0].value;
  const isDenominatorView = activeView === PAGE_SETTINGS_DENOMINATOR_VIEW;
  const activeDataset = isDenominatorView ? DENOMINATOR_DATASET : activeView;

  /** The page's datasets whose denominator drivers are not supported yet, each shown as a note. */
  const unsupportedDenominatorDatasets = useMemo(
    () => configuredDatasets.filter((ds) => !DENOMINATOR_DATASETS.includes(ds)),
    [configuredDatasets],
  );

  /** Read-only driver options for the active view: a dataset's page drivers, or the denominator drivers. */
  const driverOptions = useMemo<DriverDisplayOptionMap | null>(
    () => (isDenominatorView ? denominatorDriverOptions : (currentPageDriverOptions?.[activeDataset] ?? null)),
    [activeDataset, currentPageDriverOptions, denominatorDriverOptions, isDenominatorView],
  );

  /**
   * Handles a Page Settings selector change.
   * @param event - The Select change event carrying the new view
   */
  const handleViewChange = (event: SelectChangeEvent<PageSettingsView>) => {
    onViewChange(event.target.value as PageSettingsView);
  };

  return (
    <Stack className='page-settings-panel'>
      <FormControl className='page-settings-panel__select' size='small' fullWidth>
        <InputLabel id='page-settings-select-label'>Page Settings</InputLabel>
        <Select
          labelId='page-settings-select-label'
          label='Page Settings'
          value={activeView}
          onChange={handleViewChange}
        >
          {viewOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <List className='segments page-settings-panel__source'>
        <ListItem disablePadding>
          <ListItemText
            primary='Source'
            secondary={resolveDatasetSourceLabel(activeDataset)}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
      {driverOptions && (
        <KPIDriversDisplay
          className='page-settings-panel__drivers'
          datasetSource={activeDataset}
          driverOptions={driverOptions}
          renderMode='readonly'
        />
      )}
      {isDenominatorView && !driverOptions && (
        <Alert severity='warning' className='page-settings-panel__note'>
          This page has no denominator drivers. Set them in Page Setup.
        </Alert>
      )}
      {isDenominatorView &&
        unsupportedDenominatorDatasets.map((dataset) => (
          <Alert key={dataset} severity='info' className='page-settings-panel__note'>
            {denominatorNotEnabledMessage(dataset)}
          </Alert>
        ))}
    </Stack>
  );
};

export default PageSettingsPanel;
